import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ContactImage from '../../shared/components/ContactImage';
import { handleEnterPress } from '../../shared/helpers/util';
import {
  answerCall,
  attendedTransferCall,
  unholdCall,
} from '../reducers/calls';
import { StoreDispatch, StoreState } from '../store';
import * as styles from '../styles/call.module.scss';
import Tag from './Tag';
import TimeElapsed from './TimeElapsed';
import { HoldIcon, MuteIcon, TransferIcon } from './icons';

interface Props {
  id: string;
}

const Call: React.FC<Props> = ({ id: callId }) => {
  const dispatch = useDispatch<StoreDispatch>();
  const call = useSelector((state: StoreState) =>
    state.calls.find((c) => c.id === callId),
  );
  const armedCall = useSelector((state: StoreState) =>
    state.calls.find((c) => c.transferInitiated),
  );
  const transferInitiated = !!armedCall;

  const {
    id,
    type,
    name,
    number,
    email,
    tags,
    started,
    active,
    ringing,
    direction,
    onHold,
    muted,
  } = call || {};

  const onClick = useCallback(() => {
    if (!active) {
      if (transferInitiated || !id) {
        return;
      } else if (direction === 'IN' && ringing) {
        dispatch(answerCall(id));
      } else {
        dispatch(unholdCall(id));
      }
    }
  }, [active, direction, dispatch, id, ringing, transferInitiated]);

  let info;

  if (name) {
    info = (
      <div className={styles.info}>
        <span className={styles.name}>{name}</span>
        <span className={styles.description}>
          {number && <span className={styles.description_text}>{number}</span>}
          {(tags || []).map((t, i) => (
            <Tag key={i} className={styles.tag} label={t} />
          ))}
        </span>
      </div>
    );
  } else {
    info = (
      <div className={styles.info}>
        <span className={styles.name}>{number}</span>
      </div>
    );
  }

  // When the armed leg is outbound, only inbound calls are valid targets.
  const onlyInboundSelectable = armedCall?.direction === 'OUT';
  const showTransfer =
    transferInitiated &&
    !active &&
    (!onlyInboundSelectable || direction === 'IN');

  const inbound = direction === 'IN';

  let containerStyle = styles.container;

  if (ringing) {
    containerStyle = styles.container_ringing;
  }

  if (active) {
    containerStyle = styles.container_active;
  }

  let timeElapsedElement;
  if (started) {
    timeElapsedElement = <TimeElapsed start={started} autoUpdate />;
  } else if (inbound) {
    timeElapsedElement = <span className={styles.emphasised}>Incoming</span>;
  } else {
    timeElapsedElement = '';
  }

  return (
    <li>
      <div
        className={containerStyle}
        onClick={onClick}
        onKeyDown={handleEnterPress(onClick)}
        role='button'
        tabIndex={0}
      >
        <div className={styles.img}>
          <ContactImage email={email} type={type} />
        </div>
        {info}
        <div className={styles.status_container}>
          {muted ? <MuteIcon className={styles.status_icon} /> : ''}
          {onHold ? <HoldIcon className={styles.status_icon} /> : ''}
        </div>
        <div className={styles.time_container}>{timeElapsedElement}</div>
        <div
          className={showTransfer ? styles.slide_visible : styles.slide_hidden}
        >
          <TransferIcon
            className={styles.icon}
            onClick={() => {
              if (!id) {
                return;
              }
              dispatch(attendedTransferCall(id));
            }}
            title={`Transfer to ${name}`}
          />
        </div>
      </div>
    </li>
  );
};

export default Call;
